import { useMemo, useState } from "react";

import type { AggregatedPaper } from "@types";

interface Filters {
  search: string;
  topic: string | null;
}

interface UseFiltersResult {
  filters: Filters;
  setSearch: (value: string) => void;
  setTopic: (value: string | null) => void;
  papers: AggregatedPaper[];
}

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function matchQuery(paper: AggregatedPaper, query: string): boolean {
  if (!query) {
    return true;
  }
  const haystack = [
    paper.title,
    paper.summary,
    paper.abstract,
    paper.topic,
    paper.primary_category ?? "",
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

export function useFilters(source: AggregatedPaper[]): UseFiltersResult {
  const [filters, setFilters] = useState<Filters>({
    search: "",
    topic: null,
  });

  const papers = useMemo(() => {
    const query = normalize(filters.search);
    return source.filter((paper) => {
      const topicMatch = filters.topic ? paper.topic === filters.topic : true;
      const searchMatch = matchQuery(paper, query);
      return topicMatch && searchMatch;
    });
  }, [filters, source]);

  function setSearch(value: string): void {
    setFilters((prev) => ({ ...prev, search: value }));
  }

  function setTopic(value: string | null): void {
    setFilters((prev) => ({ ...prev, topic: value }));
  }

  return {
    filters,
    setSearch,
    setTopic,
    papers,
  };
}
